<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('api.account.accounts') }}

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'apis.accounts.create' }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('addNew') }}</span>
                        <span class="hidden-xs">{{ $t('addNew') }}</span>
                        <span class="visible-xs"><i class="fa fa-plus"></i></span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body"
                        >
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else-if="accounts.length">
                            <div class="box-header with-border">
                                <h3 class="box-title">
                                    {{ $t('api.account.accountsTable') }}
                                </h3>

                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapse = !isCollapse"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapse,
                                                'fa-minus': isCollapse,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapse">
                                <div
                                    v-if="paginationOptions.total"
                                    class="box-body with-border clearfix"
                                >
                                    <BadgeTotal
                                        :value="paginationOptions.total"
                                        color="gray"
                                        :label="`${$t('total')}: `"
                                    />

                                    <div
                                        v-if="paginationOptions.last_page > 1"
                                        class="pagination-box"
                                    >
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchAccounts"
                                        />
                                    </div>
                                </div>

                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('id') }}</th>
                                                <th style="min-width: 120px">
                                                    {{ $t('name') }}
                                                </th>
                                                <th>
                                                    {{ $t('provider.provider') }}
                                                </th>
                                                <th>{{ $t('sendingRate') }}</th>
                                                <th>{{ $t('callbackDomain') }}</th>
                                                <th>{{ $t('senderIp.senderIp') }}</th>
                                                <th>{{ $t('apisCount') }}</th>
                                                <th class="text-right">
                                                    {{ $t('createdAt') }}
                                                </th>
                                                <th class="text-right">
                                                    {{ $t('updatedAt') }}
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                                v-for="account in accounts"
                                                :key="account.id"
                                            >
                                                <td>{{ account.id }}</td>

                                                <td>{{ account.name }}</td>

                                                <td>
                                                    <ValueListEmpty
                                                        :list="[account.provider]"
                                                        block
                                                    />
                                                </td>

                                                <td>
                                                    {{ account.sendingRate }}
                                                </td>
                                                <td>
                                                    {{ account.callbackDomain }}
                                                </td>

                                                <td>
                                                     <ValueEmpty :value="account.senderIp" />
                                                </td>

                                                <td>
                                                    {{ (account.apis && account.apis.length) || 0 }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(account.createdAt) }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(account.updatedAt) }}
                                                </td>

                                                <td class="text-right text-nowrap">
                                                    <router-link
                                                        :to="{
                                                            name: 'apis.accounts.show',
                                                            params: {
                                                                id: account.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-info"
                                                        :title="$t('show')"
                                                    >
                                                        <i class="fa fa-eye" />
                                                        <span class="sr-only">
                                                            {{ $t('show') }}
                                                        </span>
                                                    </router-link>

                                                    <router-link
                                                        :to="{
                                                            name: 'apis.accounts.edit',
                                                            params: {
                                                                id: account.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-primary"
                                                        :title="$t('edit')"
                                                    >
                                                        <i class="fa fa-edit" />
                                                        <span class="sr-only">
                                                            {{ $t('edit') }}
                                                        </span>
                                                    </router-link>

                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-danger"
                                                        :title="$t('delete')"
                                                        data-toggle="modal"
                                                        data-target="#delete-modal"
                                                        @click="accountDeleting = account"
                                                    >
                                                        <i class="fa fa-trash" />
                                                        <span class="sr-only">
                                                            {{ $t('delete') }}
                                                        </span>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    v-if="paginationOptions.last_page > 1"
                                    class="box-footer clearfix"
                                >
                                    <div class="pagination-box">
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchAccounts"
                                        />
                                    </div>
                                </div>
                            </vue-slide-up-down>
                        </template>

                        <div
                            v-else
                            class="box-body with-border clearfix"
                        >
                            <p>
                                {{ $t('messages.api.account.noAccounts') }}
                                <router-link :to="{ name: 'apis.accounts.create' }">
                                    {{ $t('addNew') }}
                                </router-link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ApisAccountsModalDelete
            :account="accountDeleting"
            @deleted="onDeleted"
            @closed="accountDeleting = {}"
        />
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueListEmpty from '@/components/subcomponents/ValueListEmpty';
import ApisAccountsModalDelete from '@/components/apis/accounts/ApisAccountsModalDelete.vue';
import formatsMixin from '@/mixins/formatsMixin';
import ValueEmpty from "../../../components/subcomponents/ValueEmpty.vue";

export default {
    components: {
        ValueEmpty,
        PaginationTable,
        BadgeTotal,
        ValueListEmpty,
        ApisAccountsModalDelete,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isCollapse: true,
            isLoaded: false,
            accountDeleting: {},
            accounts: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
        };
    },
    created() {
        this.fetchAccounts(this.$route.query.page);
    },
    methods: {
        async fetchAccounts(page = 1) {
            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        accounts: { data: accounts, ...paginationOptions },
                    },
                } = await axios.get('/apis/accounts/data?page=' + page);

                this.accounts = accounts;
                this.paginationOptions = paginationOptions;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
                this.paginationOptions.disabled = false;
            }
        },
        onDeleted() {
            this.accountDeleting = {};
            this.fetchAccounts();
        },
    },
};
</script>
